import React, { useState, useMemo } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import 'react-photo-view/dist/react-photo-view.css';
import { MessageActions } from '../MessageBubble/MessageActions';

interface PictureMessageComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

export const PictureMessageComponent: React.FC<PictureMessageComponentProps> = ({
    message,
    isCurrentUser,
    onReactionClick = () => {},
    onDeleteMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    // Get images array from message
    const images = message.image || [];

    // Determine if we need to show "more" indicator
    const hasMoreImages = images.length > 4;
    const displayImages = images.slice(0, 4);
    const moreImagesCount = images.length - 4;
    const hasMessageContent = !!message.message_content;

    // Calculate grid item size based on number of images
    const getGridSize = (index: number) => {
        if (images.length === 1) return 12;
        if (images.length === 2) return 6;
        if (images.length === 3) {
            return index === 2 ? 12 : 6;
        }
        return 6;
    };

    // Memoized Image Component
    const MemoizedImage = useMemo(() => {
        return ({
            image,
            index,
            images,
            hasMessageContent,
            isLoading,
            hasMoreImages,
            moreImagesCount,
            hoveredIndex,
            setHoveredIndex,
        }: {
            image: string;
            index: number;
            images: string[];
            hasMessageContent: boolean;
            isLoading: boolean;
            hasMoreImages: boolean;
            moreImagesCount: number;
            hoveredIndex: number | null;
            setHoveredIndex: (index: number | null) => void;
        }) => {
            // Calculate border radius for each image based on position
            const getImageBorderRadius = (index: number) => {
                if (images.length === 1 && hasMessageContent) {
                    return '16px 16px 0 0';
                }
                if (images.length === 1) {
                    return '16px';
                }
                if (images.length === 2) {
                    return index === 0 ? '16px 0 0 0px' : '0 16px 0px 0';
                }
                if (images.length === 3) {
                    if (index === 0) return '16px 0 0 0';
                    if (index === 1) return '0 16px 0 0';
                    return '0 0 16px 16px';
                }
                if (images.length >= 4) {
                    if (index === 0) return '16px 0 0 0';
                    if (index === 1) return '0 16px 0 0';
                    if (index === 2) return '0 0 0 16px';
                    return '0 0 16px 0';
                }
                return '0';
            };

            return (
                <PhotoView src={image}>
                    <Box
                        sx={{
                            position: 'relative',
                            width: { xs: '100%', sm: '280px' },
                            height: images.length === 1 ? 200 : 100,
                            cursor: 'pointer',
                            overflow: 'hidden',
                        }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}>
                        <Box
                            component="img"
                            src={image}
                            alt={`Image ${index + 1}`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: getImageBorderRadius(index),
                            }}
                        />

                        {isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: getImageBorderRadius(index),
                                    zIndex: 2,
                                }}>
                                <CircularProgress size={24} sx={{ color: theme[themeStyle].true_white }} />
                            </Box>
                        )}

                        {hoveredIndex === index && !hasMoreImages && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                    borderRadius: getImageBorderRadius(index),
                                    transition: 'all 0.2s ease-in-out',
                                }}>
                                <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                            </Box>
                        )}

                        {hasMoreImages && index === 3 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: getImageBorderRadius(index),
                                }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '18px',
                                    }}>
                                    +{moreImagesCount}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </PhotoView>
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Memoized Message Content Component
    const MemoizedMessageContent = useMemo(() => {
        return ({ message }: { message: IMessage }) => {
            if (!message.message_content) return null;

            return (
                <Box
                    sx={{
                        p: 1.5,
                        borderTop: `1px solid ${theme[themeStyle].grey}30`,
                        borderBottomLeftRadius: '16px',
                        borderBottomRightRadius: '16px',
                    }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].tag_white,
                        }}>
                        {message.message_content}
                    </Typography>
                </Box>
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    // Memoized Message Footer Component
    const MemoizedMessageFooter = useMemo(() => {
        return ({
            message,
            isCurrentUser,
            onReactionClick,
            onDeleteMessage,
            formatMessageTime,
        }: {
            message: IMessage;
            isCurrentUser: boolean;
            onReactionClick: (emoji: string) => void;
            onDeleteMessage?: (messageId: string) => void;
            formatMessageTime: (timestamp?: string) => string;
        }) => {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        {formatMessageTime(message.createdAt)}
                    </Typography>

                    <MessageActions
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={onDeleteMessage}
                    />
                </Box>
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    return (
        <Box
            sx={{
                minWidth: '150px',
                maxWidth: '300px',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: theme[themeStyle].deep_grey,
                border: `1px solid ${theme[themeStyle].grey}30`,
                p: 0,
            }}>
            <PhotoProvider>
                <Grid container spacing={0.5} sx={{ p: 0 }}>
                    {displayImages.map((image, index) => (
                        <Grid item xs={getGridSize(index)} key={index}>
                            <MemoizedImage
                                image={image}
                                index={index}
                                images={images}
                                hasMessageContent={hasMessageContent}
                                isLoading={message.isLoading!}
                                hasMoreImages={hasMoreImages}
                                moreImagesCount={moreImagesCount}
                                hoveredIndex={hoveredIndex}
                                setHoveredIndex={setHoveredIndex}
                            />
                        </Grid>
                    ))}
                </Grid>
            </PhotoProvider>

            <MemoizedMessageContent message={message} />

            <MemoizedMessageFooter
                message={message}
                isCurrentUser={isCurrentUser}
                onReactionClick={onReactionClick}
                onDeleteMessage={onDeleteMessage}
                formatMessageTime={formatMessageTime}
            />
        </Box>
    );
};

// For rendering multiple images in a grid
export const PictureGrid: React.FC<{ messages: IMessage[] }> = ({ messages }) => {
    if (!messages || messages.length === 0) return null;

    // Determine grid layout based on number of images
    const getGridTemplateColumns = (count: number) => {
        if (count === 1) return '1fr';
        if (count === 2) return 'repeat(2, 1fr)';
        if (count === 3) return 'repeat(3, 1fr)';
        return 'repeat(2, 1fr)'; // Default to 2x2 grid for 4+ images
    };

    return (
        <PhotoProvider>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: getGridTemplateColumns(messages.length),
                    gridAutoRows: '1fr',
                    gap: 0,
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 1,
                }}>
                {messages.slice(0, 4).map((message, index) => (
                    <PictureMessageComponent key={index} message={message} isCurrentUser={true} />
                ))}
            </Box>
        </PhotoProvider>
    );
};
